import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '../lib/queryClient';
import { getThemesList, ThemeOutput } from '../endpoints/themes/list_GET.schema';
import { createTheme, ThemeTokens } from '../endpoints/themes/create_POST.schema';
import { updateTheme } from '../endpoints/themes/update_POST.schema';
import { deleteTheme } from '../endpoints/themes/delete_POST.schema';
import { generateTheme, GeneratedTheme } from '../endpoints/themes/generate_POST.schema';
import { generateImagePrompt } from '../endpoints/themes/generate-image-prompt_POST.schema';

// Standard paginated query for themes
export const useThemes = (sessionId: string | null, includePublic = false) => {
  return useQuery({
    queryKey: ['themes', sessionId, includePublic],
    queryFn: () =>
      getThemesList({
        sessionId: sessionId || undefined,
        limit: 20,
        offset: 0,
        includePublic,
      }),
    enabled: true, // Always enabled now (can fetch global themes)
  });
};

// Infinite scroll query for themes (for large theme libraries)
export const useInfiniteThemes = (sessionId: string | null = null, includePublic = false) => {
  return useInfiniteQuery({
    queryKey: ['themes-infinite', sessionId, includePublic],
    queryFn: ({ pageParam = 0 }) =>
      getThemesList({
        sessionId: sessionId || undefined,
        limit: 20,
        offset: pageParam,
        includePublic,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        return allPages.length * 20; // Calculate next offset
      }
      return undefined;
    },
    initialPageParam: 0,
  });
};

// Get global user themes
export const useGlobalThemes = (includePublic = false) => {
  return useThemes(null, includePublic);
};

// Get global themes with infinite scroll
export const useInfiniteGlobalThemes = (includePublic = false) => {
  return useInfiniteThemes(null, includePublic);
};

export const useCreateTheme = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTheme,
    onSuccess: () => {
      // Invalidate all theme queries (session + global)
      queryClient.invalidateQueries({ queryKey: ['themes'] });
      queryClient.invalidateQueries({ queryKey: ['themes-infinite'] });
    },
  });
};

export const useUpdateTheme = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTheme,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['themes'] });
      queryClient.invalidateQueries({ queryKey: ['themes-infinite'] });
    },
  });
};

export const useDeleteTheme = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTheme,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['themes'] });
      queryClient.invalidateQueries({ queryKey: ['themes-infinite'] });
    },
  });
};

export const useGenerateTheme = () => {
  return useMutation({
    mutationFn: generateTheme,
  });
};

export const useGenerateImagePrompt = () => {
  return useMutation({
    mutationFn: generateImagePrompt,
  });
};

export type { ThemeOutput, ThemeTokens, GeneratedTheme };
